import { ReactNode, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { Link } from 'react-router-dom';
import {
  Box,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemText,
  Paper,
  Typography,
} from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';
import AlertTitle from '@material-ui/lab/AlertTitle';
import moment from 'moment';
import { fetchOrderTrips } from './api';
import ExpiredOrderAlert from './ExpiredOrderAlert';
import { Field } from './OrderFields';
import {
  OrderDetails as OrderDetailsType,
  OrderEvent,
  OrderSummary,
  OrderTrip,
} from './types';
import {
  canConsultantReject,
  getOrderEventLabel,
  getRejectionEvent,
  isEmploymentOrder,
  isPastDecisionDeadline,
} from './utils';
import * as Styles from './Orders.css';

// Sections shared by the assignment detail page and the expanded row in All
// assignments (change requests of 07.10 and 08.10): notices, trips and
// timeline.

const formatDate = (value?: string | null) =>
  value ? moment(value).format('DD/MM/YYYY') : '—';

// Link to another assignment in the same view.
export const OrderNumberLink = ({
  number,
  consultantView = false,
}: {
  number: string;
  consultantView?: boolean;
}) => (
  <Link
    to={`${
      consultantView ? '/my-assignments' : '/assignments'
    }/${encodeURIComponent(number)}`}
  >
    {number}
  </Link>
);

// Who rejected the order, when and why, with the next step for the issuer.
// Older data without the event still says that the order was rejected.
const RejectionInfo = ({
  rejection,
  consultantView,
}: {
  rejection: OrderEvent | null;
  consultantView: boolean;
}) => {
  const intl = useIntl();
  const date = rejection
    ? moment(rejection.occurredAt).format('DD/MM/YYYY HH:mm')
    : '';
  const name = rejection?.actorName || rejection?.actorUsername;
  let summary = intl.formatMessage({ id: 'ORDERS.REJECTION.NO_DETAILS' });
  if (rejection && consultantView) {
    summary = intl.formatMessage(
      { id: 'MY_ORDERS.REJECTION.BY_YOU' },
      { date },
    );
  } else if (rejection && name) {
    summary = intl.formatMessage({ id: 'ORDERS.REJECTION.BY' }, { name, date });
  } else if (rejection) {
    summary = intl.formatMessage({ id: 'ORDERS.REJECTION.AT' }, { date });
  }
  return (
    <Alert severity="warning">
      <AlertTitle>
        {intl.formatMessage({ id: 'ORDERS.EVENT.REJECTED' })}
      </AlertTitle>
      <div>{summary}</div>
      {rejection?.reason && (
        <Styles.Conditions>
          {intl.formatMessage(
            { id: 'ORDERS.REJECTION.REASON' },
            { reason: rejection.reason },
          )}
        </Styles.Conditions>
      )}
      {!consultantView && (
        <div>{intl.formatMessage({ id: 'ORDERS.REJECTION.NEXT_STEP' })}</div>
      )}
    </Alert>
  );
};

// Notices above the order's fields: a pending decision, a rejection, an
// expired deadline and, for the consultant, employment and late acceptance.
export const OrderNotices = ({
  details,
  consultantView,
  spacing = 2,
}: {
  details: OrderDetailsType;
  consultantView: boolean;
  spacing?: number;
}) => {
  const intl = useIntl();
  const { order } = details;
  // B2B and civil-contract orders waiting for the consultant's decision.
  const awaitingDecision = canConsultantReject(order);
  const lateAcceptance =
    order.lifecycleStatus === 'ACCEPTED' &&
    order.activationDate &&
    moment(order.activationDate).isAfter(moment(order.startDate), 'day');
  return (
    <>
      {awaitingDecision && (
        <Box mb={spacing}>
          <Alert severity="info">
            {intl.formatMessage(
              {
                id: consultantView
                  ? 'MY_ORDERS.DECISION_INFO'
                  : 'ORDERS.PENDING_DECISION_INFO',
              },
              { date: moment(order.decisionDeadline).format('DD/MM/YYYY') },
            )}
          </Alert>
        </Box>
      )}
      {order.lifecycleStatus === 'REJECTED' && (
        <Box mb={spacing}>
          <RejectionInfo
            rejection={getRejectionEvent(details.events || [])}
            consultantView={consultantView}
          />
        </Box>
      )}
      {consultantView && isEmploymentOrder(order) && (
        <Box mb={spacing}>
          <Alert severity="info">
            {intl.formatMessage({ id: 'MY_ORDERS.NO_DECISION_NEEDED' })}
          </Alert>
        </Box>
      )}
      {isPastDecisionDeadline(order) && (
        <Box mb={spacing}>
          <ExpiredOrderAlert order={order} consultantView={consultantView} />
        </Box>
      )}
      {consultantView && lateAcceptance && (
        <Box mb={spacing}>
          <Alert severity="info">
            {intl.formatMessage(
              { id: 'MY_ORDERS.LATE_ACCEPTED' },
              { date: moment(order.activationDate).format('DD/MM/YYYY') },
            )}
          </Alert>
        </Box>
      )}
    </>
  );
};

type TripsState =
  | { status: 'loading' | 'error' | 'unavailable' }
  | { status: 'ready'; trips: OrderTrip[] };

// Up to this many trips are listed; more get one link to the Trips module.
export const MAX_LISTED_TRIPS = 5;

const tripsUrl = (order: OrderSummary, consultantView: boolean) => {
  const params = new URLSearchParams({
    scope: consultantView ? 'mine' : 'all',
    show: 'all',
  });
  if (!consultantView) {
    params.set('employeeId', String(order.consultantId));
    params.set('employeeName', order.consultantName);
  }
  params.set('clientId', String(order.clientId));
  params.set('clientName', order.clientName);
  return `/trips?${params.toString()}`;
};

// Business trips linked to the order as one field of the details grid, one
// trip per line (change request of 08.10). Hidden when the backend does not
// offer the endpoint.
export const OrderTripsField = ({
  order,
  consultantView,
}: {
  order: OrderSummary;
  consultantView: boolean;
}) => {
  const intl = useIntl();
  const [state, setState] = useState<TripsState>({ status: 'loading' });
  const { orderNumber } = order;

  useEffect(() => {
    let active = true;
    setState({ status: 'loading' });
    Promise.resolve(fetchOrderTrips(orderNumber, consultantView)).then(
      (result) => {
        if (!active) {
          return;
        }
        if (result?.ok && Array.isArray(result.data)) {
          setState({ status: 'ready', trips: result.data });
        } else if (!result || [403, 404, 405].includes(result.status)) {
          setState({ status: 'unavailable' });
        } else {
          setState({ status: 'error' });
        }
      },
    );
    return () => {
      active = false;
    };
  }, [consultantView, orderNumber]);

  if (state.status === 'unavailable') {
    return null;
  }

  const trips = state.status === 'ready' ? state.trips : [];
  let value: ReactNode = null;
  if (state.status === 'loading') {
    value = (
      <CircularProgress
        size={16}
        aria-label={intl.formatMessage({ id: 'ORDERS.LOADING' })}
      />
    );
  } else if (state.status === 'error') {
    value = intl.formatMessage({ id: 'ORDERS.TRIPS_ERROR' });
  } else if (trips.length > MAX_LISTED_TRIPS) {
    value = (
      <Link to={tripsUrl(order, consultantView)}>
        {intl.formatMessage(
          { id: 'ORDERS.TRIPS_SHOW_ALL' },
          { count: trips.length },
        )}
      </Link>
    );
  } else if (trips.length) {
    value = trips.map((trip) => (
      <div key={trip.id}>
        <Link to={`/trips?id=${trip.id}`}>
          {[trip.city, trip.country].filter(Boolean).join(', ') ||
            trip.title ||
            intl.formatMessage({ id: 'ORDERS.TRIP_UNTITLED' })}
        </Link>{' '}
        · {formatDate(trip.startDate)} – {formatDate(trip.endDate)}
      </div>
    ));
  }

  return (
    <Field label={intl.formatMessage({ id: 'ORDERS.TRIPS' })} value={value} />
  );
};

// Lifecycle events of the order, oldest first as the backend returns them.
export const OrderTimeline = ({
  details,
  consultantView,
}: {
  details: OrderDetailsType;
  consultantView: boolean;
}) => {
  const intl = useIntl();
  const events = details.events || [];
  const employmentOrder = isEmploymentOrder(details.order);
  return (
    <Box mt={2} component={Paper} px={3} py={2}>
      <Typography variant="h6">
        {intl.formatMessage({ id: 'ORDERS.TIMELINE' })}
      </Typography>
      <List>
        {events.map((event, index) => {
          const issuedWithoutDecision =
            employmentOrder && event.eventType === 'ACCEPTED';
          // The automatic acceptance reason only repeats the event label.
          const hiddenInternalReason =
            issuedWithoutDecision ||
            (consultantView &&
              ['CANCELLED', 'EARLY_ENDED'].includes(event.eventType));
          const actor =
            event.actorName ||
            event.actorUsername ||
            intl.formatMessage({ id: 'ORDERS.SYSTEM' });
          const metadata = [
            actor,
            moment(event.occurredAt).format('DD/MM/YYYY HH:mm'),
          ];
          if (event.activationDate) {
            metadata.push(
              `${intl.formatMessage({
                id: 'ORDERS.ACTIVATION_DATE',
              })}: ${moment(event.activationDate).format('DD/MM/YYYY')}`,
            );
          }
          if (event.effectiveEndDate) {
            metadata.push(
              `${intl.formatMessage({
                id: 'ORDERS.EFFECTIVE_END_DATE',
              })}: ${moment(event.effectiveEndDate).format('DD/MM/YYYY')}`,
            );
          }
          if (event.relatedOrderNumber) {
            metadata.push(
              `${intl.formatMessage({ id: 'ORDERS.RELATED_ORDER' })}: ${
                event.relatedOrderNumber
              }`,
            );
          }
          if (event.reason && !hiddenInternalReason) {
            metadata.push(event.reason);
          }
          return (
            <div key={event.id}>
              <ListItem alignItems="flex-start">
                <ListItemText
                  primary={intl.formatMessage({
                    id: getOrderEventLabel(
                      event.eventType,
                      issuedWithoutDecision,
                    ),
                  })}
                  secondary={metadata.join(' · ')}
                />
              </ListItem>
              {index < events.length - 1 && <Divider />}
            </div>
          );
        })}
      </List>
    </Box>
  );
};
